// src/pages/AdminCoupons.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { resolveDisplayCode } from '../utils/redeemDisplayCode';
import { buildCouponClaimPath, buildCouponLiffUrl } from '../utils/couponLiff';

axios.defaults.withCredentials = true;

const http = axios.create({ withCredentials: true });
http.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem('admin_token');
    if (t) config.headers.Authorization = `Bearer ${t}`;
  } catch { /* ignore */ }
  return config;
});

const randomCode = () =>
  Math.random().toString(36).slice(2, 10).toUpperCase();

const EMPTY_FORM = {
  NAME:'', NAME_EN:'', CODE:'', START_AT:'', END_AT:'', ACTIVE:true, DESCRIPTION:'', DESCRIPTION_EN:'',
  DISCOUNT_TYPE:'', DISCOUNT_VALUE:'', MIN_ORDER_AMOUNT:'',
};

const ERROR_TEXT = {
  CODE_EXISTS: 'CODE 已存在',
  END_BEFORE_START: '結束日早於開始日',
  BAD_DISCOUNT_VALUE: '折扣值不正確（百分比需 1–100）',
  BAD_NUMBER: '數字欄位格式不正確',
  BAD_DATE: '日期格式不正確',
  MISSING_FIELDS: '請填名稱與起訖日',
  UNAUTHORIZED: '登入已過期，請重新登入',
};
const errText = (e, fallback) => ERROR_TEXT[e?.response?.data?.error] || fallback;

const discountSummary = (r) => {
  const v = Number(r.DISCOUNT_VALUE);
  if (!r.DISCOUNT_TYPE || !Number.isFinite(v) || v <= 0) return '—';
  return r.DISCOUNT_TYPE === 'percent' ? `${v}%` : `NT$ ${v}`;
};

function DiscountInputs({ value, onChange }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
      <select value={value.DISCOUNT_TYPE || ''} onChange={(e)=>onChange('DISCOUNT_TYPE', e.target.value)}>
        <option value="">不折扣</option>
        <option value="amount">折抵金額</option>
        <option value="percent">折扣 %</option>
      </select>
      {value.DISCOUNT_TYPE && (
        <>
          <input type="number" min="0" value={value.DISCOUNT_VALUE} onChange={(e)=>onChange('DISCOUNT_VALUE', e.target.value)} placeholder={value.DISCOUNT_TYPE === 'percent' ? '%' : '金額'} />
          <input type="number" min="0" value={value.MIN_ORDER_AMOUNT} onChange={(e)=>onChange('MIN_ORDER_AMOUNT', e.target.value)} placeholder="最低消費（選填）" />
        </>
      )}
    </div>
  );
}

const discountPayload = (f) => ({
  DISCOUNT_TYPE: f.DISCOUNT_TYPE || null,
  DISCOUNT_VALUE: f.DISCOUNT_TYPE && f.DISCOUNT_VALUE !== '' ? Number(f.DISCOUNT_VALUE) : null,
  MIN_ORDER_AMOUNT: f.DISCOUNT_TYPE && f.MIN_ORDER_AMOUNT !== '' ? Number(f.MIN_ORDER_AMOUNT) : null,
});

export default function AdminCoupons(){
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingForm, setEditingForm] = useState({});

  // 新增用
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(()=>{ load(); },[]);
  const load = async () => {
    try{
      setLoading(true);
      const { data } = await http.get('/api/admin/coupons');
      const fetched = Array.isArray(data?.rows) ? data.rows : (Array.isArray(data) ? data : []);
      setRows(fetched);
      setEditingId(null);
      setEditingForm({});
    }catch{
      setRows([]);
    }finally{
      setLoading(false);
    }
  };

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); setMsg('已複製'); }
    catch { setMsg('複製失敗'); }
    setTimeout(()=>setMsg(''), 1200);
  };

  const makeClaimUrlByCode = (code) =>
    `${window.location.origin}${buildCouponClaimPath(code || '')}`;
  const makeLineClaimUrlByCode = (code) => buildCouponLiffUrl(code || '').universal;
  const [claimsModal, setClaimsModal] = useState(null);
  const [deletingClaimId, setDeletingClaimId] = useState(null);

  const openClaimQr = (code) => {
    const url = makeClaimUrlByCode(code);
    window.open(`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(url)}`, '_blank', 'noopener,noreferrer');
  };

  const loadClaims = async (row) => {
    try {
      const { data } = await http.get(`/api/admin/coupons/${row.ID}/claims`);
      setClaimsModal({ coupon: row, rows: Array.isArray(data?.rows) ? data.rows : [] });
    } catch {
      setMsg('無法載入領取明細');
      setTimeout(() => setMsg(''), 2000);
    }
  };

  const deleteClaim = async (cl) => {
    const code = resolveDisplayCode(cl.CLAIM_ID, cl.DISPLAY_CODE);
    const status = (cl.IS_USED || cl.USED_AT) ? '已兌換' : '未兌換';
    const contact = cl.PHONE || cl.EMAIL || '—';
    const ok = window.confirm(
      `確定刪除此領取紀錄？\n\n兌換編號：${code}\n${contact}\n狀態：${status}\n\n刪除後該手機/Email 可再次領券。`
    );
    if (!ok) return;
    setDeletingClaimId(cl.CLAIM_ID);
    try {
      await http.delete(`/api/admin/coupon/claims/${cl.CLAIM_ID}`);
      setClaimsModal((prev) => prev ? {
        ...prev,
        rows: prev.rows.filter((r) => String(r.CLAIM_ID) !== String(cl.CLAIM_ID)),
      } : prev);
      setMsg('已刪除領取紀錄');
      setTimeout(() => setMsg(''), 1200);
    } catch (e) {
      const err = e?.response?.data?.error;
      setMsg(err === 'FK_BLOCKED' ? '無法刪除：請先處理關聯訂單' : '刪除失敗');
      setTimeout(() => setMsg(''), 2000);
    } finally {
      setDeletingClaimId(null);
    }
  };

  const startEditRow = (row) => {
    setEditingId(row.ID);
    setEditingForm({
      NAME: row.NAME || '',
      NAME_EN: row.NAME_EN || '',
      CODE: row.CODE || '',
      DESCRIPTION: row.DESCRIPTION || '',
      DESCRIPTION_EN: row.DESCRIPTION_EN || '',
      START_AT: (row.START_AT || '').slice(0,10),
      END_AT: (row.END_AT || '').slice(0,10),
      ACTIVE: !!row.ACTIVE,
      DISCOUNT_TYPE: row.DISCOUNT_TYPE || '',
      DISCOUNT_VALUE: row.DISCOUNT_VALUE ?? '',
      MIN_ORDER_AMOUNT: row.MIN_ORDER_AMOUNT ?? '',
    });
  };

  const cancelEditRow = () => {
    setEditingId(null);
    setEditingForm({});
  };

  const onEditField = (key, value) => {
    setEditingForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveEditingRow = async () => {
    if (!editingId) return;
    try {
      await http.patch(`/api/admin/coupons/${editingId}`, {
        NAME: editingForm.NAME,
        NAME_EN: editingForm.NAME_EN,
        CODE: editingForm.CODE,
        DESCRIPTION: editingForm.DESCRIPTION,
        DESCRIPTION_EN: editingForm.DESCRIPTION_EN,
        START_AT: editingForm.START_AT,
        END_AT: editingForm.END_AT,
        ACTIVE: editingForm.ACTIVE ? 1 : 0,
        ...discountPayload(editingForm),
      });
      setMsg('已更新優惠券'); setTimeout(()=>setMsg(''), 1200);
      setEditingId(null);
      setEditingForm({});
      load();
    } catch (e) {
      setMsg(errText(e, '更新優惠券失敗')); setTimeout(()=>setMsg(''), 2000);
    }
  };

  const toggleActive = async (id, active) => {
    try{
      await http.patch(`/api/admin/coupons/${id}`, { ACTIVE: active?1:0 });
      load();
    }catch{
      setMsg('更新狀態失敗'); setTimeout(()=>setMsg(''), 1500);
    }
  };

  const create = async () => {
    const payload = {
      NAME: (form.NAME||'').trim(),
      NAME_EN: (form.NAME_EN||'').trim(),
      CODE: (form.CODE||'').trim(),
      START_AT: form.START_AT || null,
      END_AT: form.END_AT || null,
      ACTIVE: form.ACTIVE ? 1 : 0,
      DESCRIPTION: form.DESCRIPTION || '',
      DESCRIPTION_EN: form.DESCRIPTION_EN || '',
      ...discountPayload(form),
    };
    if (!payload.NAME) { setMsg('請輸入名稱'); setTimeout(()=>setMsg(''), 1200); return; }
    try{
      await http.post('/api/admin/coupons', payload);
      setForm(EMPTY_FORM);
      load(); setMsg('已新增'); setTimeout(()=>setMsg(''), 1200);
    }catch (e) {
      setMsg(errText(e, '新增失敗')); setTimeout(()=>setMsg(''), 2000);
    }
  };

  return (
    <div>
      <h3>優惠券列表</h3>
      <p style={{ fontSize: 13, opacity: 0.75, margin: '0 0 12px' }}>
        一般連結：瀏覽器填電話+信箱一鍵領取；LINE 連結：在 LINE App 內開啟自動領取並推播
      </p>
      {loading ? <div>載入中…</div> : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', minWidth:1080 }}>
          <thead>
            <tr>
              <th>ID</th>
              <th>名稱</th>
              <th>CODE</th>
              <th>有效期</th>
              <th>折扣</th>
              <th>啟用</th>
              <th>備註</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.ID}>
                <td>{r.ID}</td>
                <td>
                  {editingId === r.ID ? (
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      <input
                        value={editingForm.NAME}
                        onChange={(e)=>onEditField('NAME', e.target.value)}
                        style={{width:'100%'}}
                        placeholder="名稱"
                      />
                      <input
                        value={editingForm.NAME_EN}
                        onChange={(e)=>onEditField('NAME_EN', e.target.value)}
                        style={{width:'100%'}}
                        placeholder="English name（選填）"
                      />
                    </div>
                  ) : (
                    <div>
                      <div>{r.NAME}</div>
                      {r.NAME_EN && <div style={{ fontSize:12, opacity:.65 }}>{r.NAME_EN}</div>}
                    </div>
                  )}
                </td>
                <td>
                  {editingId === r.ID ? (
                    <input
                      value={editingForm.CODE}
                      onChange={(e)=>onEditField('CODE', e.target.value)}
                      style={{width:'100%'}}
                    />
                  ) : (r.CODE || '—')}
                </td>
                <td>
                  {editingId === r.ID ? (
                    <div style={{display:'flex', flexDirection:'column', gap:4}}>
                      <input type="date" value={editingForm.START_AT} onChange={(e)=>onEditField('START_AT', e.target.value)} />
                      <input type="date" value={editingForm.END_AT} onChange={(e)=>onEditField('END_AT', e.target.value)} />
                    </div>
                  ) : (
                    `${(r.START_AT||'').slice(0,10)} ~ ${(r.END_AT||'').slice(0,10)}`
                  )}
                </td>
                <td>
                  {editingId === r.ID ? (
                    <DiscountInputs value={editingForm} onChange={onEditField} />
                  ) : (
                    <div>
                      <div>{discountSummary(r)}</div>
                      {r.MIN_ORDER_AMOUNT > 0 && <div style={{ fontSize:12, opacity:.65 }}>滿 {r.MIN_ORDER_AMOUNT}</div>}
                    </div>
                  )}
                </td>
                <td>
                  {editingId === r.ID ? (
                    <label style={{display:'flex',alignItems:'center',gap:6}}>
                      <input type="checkbox" checked={!!editingForm.ACTIVE} onChange={(e)=>onEditField('ACTIVE', e.target.checked)} />
                      {editingForm.ACTIVE ? '啟用' : '停用'}
                    </label>
                  ) : (
                    <label style={{display:'inline-flex', alignItems:'center', gap:6}}>
                      <input type="checkbox" checked={!!r.ACTIVE} onChange={e=>toggleActive(r.ID, e.target.checked)} />
                      {r.ACTIVE? '啟用':'停用'}
                    </label>
                  )}
                </td>
                <td>
                  {editingId === r.ID ? (
                    <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                      <textarea
                        value={editingForm.DESCRIPTION}
                        onChange={(e)=>onEditField('DESCRIPTION', e.target.value)}
                        rows={2}
                        style={{width:'100%'}}
                        placeholder="備註"
                      />
                      <textarea
                        value={editingForm.DESCRIPTION_EN}
                        onChange={(e)=>onEditField('DESCRIPTION_EN', e.target.value)}
                        rows={2}
                        style={{width:'100%'}}
                        placeholder="English note（選填）"
                      />
                    </div>
                  ) : (
                    <div style={{whiteSpace:'pre-wrap'}}>
                      {r.DESCRIPTION || <span style={{opacity:.6}}>（無備註）</span>}
                      {r.DESCRIPTION_EN && (
                        <div style={{ fontSize:12, opacity:.65, marginTop:4 }}>{r.DESCRIPTION_EN}</div>
                      )}
                    </div>
                  )}
                </td>
                <td style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                  {editingId === r.ID ? (
                    <>
                      <button onClick={saveEditingRow}>儲存</button>
                      <button onClick={cancelEditRow}>取消</button>
                    </>
                  ) : (
                    <>
                      <button onClick={()=>copy(makeClaimUrlByCode(r.CODE))} disabled={!r.CODE}>複製連結</button>
                      <button onClick={()=>copy(makeLineClaimUrlByCode(r.CODE))} disabled={!r.CODE}>複製LINE連結</button>
                      <button onClick={()=>openClaimQr(r.CODE)} disabled={!r.CODE}>領券QR</button>
                      <button onClick={()=>loadClaims(r)}>領取明細</button>
                      <button onClick={()=>startEditRow(r)}>編輯</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length===0 && (
              <tr><td colSpan={8} style={{padding:8, opacity:.7}}>尚無資料</td></tr>
            )}
          </tbody>
        </table>
        </div>
      )}

      <h3 style={{marginTop:24}}>新增優惠券</h3>
      <div style={{display:'grid', gridTemplateColumns:'repeat(3, minmax(0, 1fr))', gap:8, alignItems:'end', marginBottom:8}}>
        <div>
          <label style={{fontSize:12, opacity:.7}}>名稱</label>
          <input value={form.NAME} onChange={e=>setForm(f=>({...f, NAME:e.target.value}))} style={{width:'100%'}}/>
        </div>
        <div>
          <label style={{fontSize:12, opacity:.7}}>英文名稱（選填）</label>
          <input value={form.NAME_EN} onChange={e=>setForm(f=>({...f, NAME_EN:e.target.value}))} style={{width:'100%'}}/>
        </div>
        <div>
          <label style={{fontSize:12, opacity:.7, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
            <span>CODE（選填）</span>
            <button type="button" style={{fontSize:10}} onClick={()=>setForm(f=>({...f, CODE: randomCode()}))}>產生亂碼</button>
          </label>
          <input value={form.CODE} onChange={e=>setForm(f=>({...f, CODE:e.target.value}))} style={{width:'100%'}}/>
        </div>
      </div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(6, minmax(0, 1fr)) auto', gap:8, alignItems:'end'}}>
        <div>
          <label style={{fontSize:12, opacity:.7}}>開始日</label>
          <input type="date" value={form.START_AT} onChange={e=>setForm(f=>({...f, START_AT:e.target.value}))} style={{width:'100%'}}/>
        </div>
        <div>
          <label style={{fontSize:12, opacity:.7}}>結束日</label>
          <input type="date" value={form.END_AT} onChange={e=>setForm(f=>({...f, END_AT:e.target.value}))} style={{width:'100%'}}/>
        </div>
        <div>
          <label style={{fontSize:12, opacity:.7}}>折扣</label>
          <DiscountInputs value={form} onChange={(k, v)=>setForm(f=>({...f, [k]: v}))} />
        </div>
        <div>
          <label style={{fontSize:12, opacity:.7}}>備註</label>
          <textarea value={form.DESCRIPTION} onChange={e=>setForm(f=>({...f, DESCRIPTION:e.target.value}))} style={{width:'100%'}} rows={2}/>
        </div>
        <div>
          <label style={{fontSize:12, opacity:.7}}>英文備註（選填）</label>
          <textarea value={form.DESCRIPTION_EN} onChange={e=>setForm(f=>({...f, DESCRIPTION_EN:e.target.value}))} style={{width:'100%'}} rows={2}/>
        </div>
        <div>
          <label style={{fontSize:12, opacity:.7, display:'block'}}>啟用</label>
          <input type="checkbox" checked={form.ACTIVE} onChange={e=>setForm(f=>({...f, ACTIVE:e.target.checked}))}/>
        </div>
        <div>
          <button onClick={create}>新增</button>
        </div>
      </div>

      {msg && <div style={{marginTop:10, color:'#0a7', fontWeight:700}}>{msg}</div>}

      {claimsModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.4)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={()=>setClaimsModal(null)} role="presentation">
          <div style={{ background:'#fff', borderRadius:12, padding:16, maxWidth:720, width:'100%', maxHeight:'80vh', overflow:'auto' }} onClick={(e)=>e.stopPropagation()} role="dialog">
            <h3 style={{ marginTop:0 }}>{claimsModal.coupon?.NAME} — 領取明細</h3>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>
                  <th align="left" style={{ padding:8 }}>兌換編號</th>
                  <th align="left" style={{ padding:8 }}>手機</th>
                  <th align="left" style={{ padding:8 }}>Email</th>
                  <th align="left" style={{ padding:8 }}>狀態</th>
                  <th align="left" style={{ padding:8 }}>領取時間</th>
                  <th align="left" style={{ padding:8 }}>兌換時間</th>
                  <th align="left" style={{ padding:8 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {claimsModal.rows.map((cl) => (
                  <tr key={String(cl.CLAIM_ID)}>
                    <td style={{ padding:8, fontFeatureSettings:'"tnum"' }}>
                      {resolveDisplayCode(cl.CLAIM_ID, cl.DISPLAY_CODE)}
                    </td>
                    <td style={{ padding:8 }}>{cl.PHONE || '—'}</td>
                    <td style={{ padding:8 }}>{cl.EMAIL || '—'}</td>
                    <td style={{ padding:8 }}>{(cl.IS_USED || cl.USED_AT) ? '已兌換' : '未兌換'}</td>
                    <td style={{ padding:8 }}>{cl.CLAIMED_AT ? String(cl.CLAIMED_AT).replace('T',' ').slice(0,19) : '—'}</td>
                    <td style={{ padding:8 }}>{cl.USED_AT ? String(cl.USED_AT).replace('T',' ').slice(0,19) : '—'}</td>
                    <td style={{ padding:8 }}>
                      <button
                        type="button"
                        disabled={deletingClaimId === cl.CLAIM_ID}
                        onClick={() => deleteClaim(cl)}
                        style={{ color: '#b00020' }}
                      >
                        {deletingClaimId === cl.CLAIM_ID ? '刪除中…' : '刪除'}
                      </button>
                    </td>
                  </tr>
                ))}
                {claimsModal.rows.length === 0 && (
                  <tr><td colSpan={7} style={{ padding:8, opacity:.6 }}>尚無領取紀錄</td></tr>
                )}
              </tbody>
            </table>
            <button type="button" style={{ marginTop:12 }} onClick={()=>setClaimsModal(null)}>關閉</button>
          </div>
        </div>
      )}
    </div>
  );
}
